import cx from "classnames";
import { type PropsWithChildren } from "react";
import { useMediaQuery } from "react-responsive";

import {
  Heading,
  type ProgressStep,
  ProgressStepper,
} from "@icg360/design-system";

import {
  MultiStepFormButtons,
  type MultiStepFormButtonsProps,
} from "components/multi-step-form-layout/multi-step-form-buttons";
import { screenSizes } from "consts";
import SageSureLogo from "images/sagesure-logo.svg?react";

import styles from "./multi-step-form-layout.module.scss";

type MultiStepFormLayoutProps = PropsWithChildren<{
  showLogo?: boolean;
  labels?: string[];
  step: number;
  totalSteps: number;
  heading: string;
  subheading?: string;
  buttonProps?: MultiStepFormButtonsProps;
  noScroll?: boolean;
}>;

export const MultiStepFormLayout = ({
  showLogo = false,
  labels = [],
  step,
  totalSteps,
  heading,
  subheading,
  buttonProps,
  noScroll,
  children,
}: MultiStepFormLayoutProps) => {
  const isMobile = useMediaQuery({
    maxWidth: screenSizes.tabletS,
  });
  const steps: ProgressStep[] = [];
  for (let i = 1; i <= totalSteps; i++) {
    if (i < step) {
      steps.push({ status: "completed", label: labels[i - 1] });
    } else if (i === step) {
      steps.push({ status: "in-progress", label: labels[i - 1] });
    } else {
      steps.push({ status: "incomplete", label: labels[i - 1] });
    }
  }
  return (
    <div
      className={cx(
        styles.layoutWrapper,
        showLogo && styles.layoutWithLogo,
        buttonProps && styles.layoutWithFooter,
      )}
    >
      {showLogo ? (
        <div className={styles.logoWrapper}>
          <SageSureLogo height="28" />
        </div>
      ) : null}
      <div className={cx(styles.layoutContent, noScroll && styles.noScroll)}>
        <div className={styles.progressStepperWrapper}>
          <ProgressStepper
            appearance={isMobile ? "count" : "default"}
            steps={steps}
          />
        </div>
        <div>
          <Heading size="lg">{heading}</Heading>
          {subheading ? (
            <p className={styles.layoutSubheading}>{subheading}</p>
          ) : null}
        </div>
        {children}
      </div>
      {buttonProps ? <MultiStepFormButtons {...buttonProps} /> : null}
    </div>
  );
};
